import { useState, useRef, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router";
import { Menu, X, ChevronDown } from "lucide-react";
import { LinkButton } from "../components/ui";
import { APP_URL } from "../lib/env";

const PRIMARY_NAV: Array<{ label: string; to?: string; href?: string; children?: Array<{ label: string; to: string; desc?: string }> }> = [
  {
    label: "Platform",
    to: "/platform",
    children: [
      { label: "Platform overview", to: "/platform", desc: "End-to-end workflow + architecture" },
      { label: "Features", to: "/features", desc: "Clinical engine · synthesis · bundles · invoicing" },
    ],
  },
  {
    label: "Solutions",
    children: [
      { label: "Long-Term Care (SNF & ALF)", to: "/solutions/snf", desc: "ASCP · F605 / F757 / F881 · same pack for assisted living" },
      { label: "Ambulatory Surgical (ASC)", to: "/solutions/asc", desc: "AAAHC pharma standards · DEA · vaccine temp" },
      { label: "Dialysis", to: "/solutions/dialysis", desc: "CMS CfC §494 · AAMI water" },
    ],
  },
  { label: "Pricing", to: "/pricing" },
  {
    label: "Resources",
    children: [
      { label: "Resource hub", to: "/resources" },
      { label: "Blog", to: "/blog" },
      { label: "Docs", to: "/docs" },
      { label: "Trust center", to: "/trust" },
    ],
  },
  { label: "About", to: "/about" },
];

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const loc = useLocation();

  // Dropdown open/close with a small close delay so moving the cursor from a
  // section to its submenu never makes the menu vanish mid-move.
  const closeTimer = useRef<number | null>(null);
  const openNow = (label: string) => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenMenu(label);
  };
  const closeSoon = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenMenu(null), 180);
  };
  // Reliable close: Escape or a click outside any dropdown. (The trigger itself
  // never toggles closed, which is what caused the "click twice to stick" bug.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenMenu(null);
    };
    const onDown = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest("[data-nav-dd]")) setOpenMenu(null);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, []);

  return (
    <header className="sticky top-0 z-40 bg-canvas/90 backdrop-blur supports-[backdrop-filter]:bg-canvas/75 border-b border-border">
      <div className="mx-auto max-w-container px-6 md:px-8">
        <div className="flex h-16 items-center justify-between gap-6">
          {/* Logo */}
          <Link
            to="/"
            aria-label="WeConsult Rx home"
            className="flex items-center"
            onClick={() => setMobileOpen(false)}
          >
            <img
              src="/logo-horizontal-no-tagline.svg"
              alt="WeConsult Rx"
              width="220"
              height="48"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop nav */}
          <nav aria-label="Primary" className="hidden lg:flex items-center gap-1">
            {PRIMARY_NAV.map((item) => {
              if (item.children) {
                const isOpen = openMenu === item.label;
                return (
                  <div
                    key={item.label}
                    className="relative"
                    data-nav-dd
                    onMouseEnter={() => openNow(item.label)}
                    onMouseLeave={closeSoon}
                  >
                    {item.to ? (
                      // Section with its own overview page: clicking navigates
                      // there; hover or keyboard focus reveals the submenu.
                      <NavLink
                        to={item.to}
                        className={({ isActive }) =>
                          `inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md ${
                            isActive || isOpen
                              ? "text-ink-900 font-semibold"
                              : "text-ink-700 hover:text-ink-900"
                          }`
                        }
                        aria-expanded={isOpen}
                        aria-haspopup="true"
                        onFocus={() => openNow(item.label)}
                      >
                        {item.label}
                        <ChevronDown className="h-4 w-4" aria-hidden />
                      </NavLink>
                    ) : (
                      // Pure group (no page): click/hover/focus opens it. It never
                      // toggles closed on the trigger — Escape, an outside click,
                      // or moving away closes it.
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-ink-700 hover:text-ink-900 rounded-md"
                        aria-expanded={isOpen}
                        aria-haspopup="true"
                        onFocus={() => openNow(item.label)}
                        onClick={() => openNow(item.label)}
                      >
                        {item.label}
                        <ChevronDown className="h-4 w-4" aria-hidden />
                      </button>
                    )}
                    {isOpen ? (
                      // top-full + pt-2 keeps the menu's hit area touching the
                      // trigger (no dead gap); re-entering it cancels the close
                      // timer, so the submenu stays put while you move to it.
                      <div
                        role="menu"
                        onMouseEnter={() => openNow(item.label)}
                        onMouseLeave={closeSoon}
                        className="absolute left-0 top-full pt-2 w-72"
                      >
                        <div className="rounded-xl bg-surface border border-border shadow-pop p-2">
                          {item.children.map((c) => (
                            <NavLink
                              key={c.to}
                              to={c.to}
                              role="menuitem"
                              onClick={() => setOpenMenu(null)}
                              className={({ isActive }) =>
                                `block rounded-lg px-3 py-2.5 hover:bg-forest-50 ${
                                  isActive ? "bg-forest-50" : ""
                                }`
                              }
                            >
                              <div className="text-sm font-semibold text-ink-900">{c.label}</div>
                              {c.desc ? <div className="text-xs text-ink-600 mt-0.5">{c.desc}</div> : null}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.label}
                  to={item.to!}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium rounded-md ${
                      isActive
                        ? "text-ink-900 font-semibold"
                        : "text-ink-700 hover:text-ink-900"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          {/* CTA + Sign In */}
          <div className="hidden lg:flex items-center gap-2">
            <LinkButton href={APP_URL} variant="ghost" size="md">
              Sign in
            </LinkButton>
            <LinkButton to="/demo" variant="primary" size="md">
              Request a demo
            </LinkButton>
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center rounded-md p-2 text-ink-700"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen ? (
        <nav
          aria-label="Mobile primary"
          className="lg:hidden border-t border-border bg-canvas"
          key={loc.pathname}
        >
          <div className="mx-auto max-w-container px-6 py-4 space-y-1">
            {PRIMARY_NAV.flatMap((i) =>
              i.children
                ? i.children.map((c) => ({ label: c.label, to: c.to }))
                : [{ label: i.label, to: i.to! }]
            ).map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `block rounded-md px-3 py-2.5 text-sm font-medium ${
                    isActive
                      ? "bg-ink-50 text-ink-900 font-semibold"
                      : "text-ink-700 hover:bg-ink-50"
                  }`
                }
              >
                {c.label}
              </NavLink>
            ))}
            <div className="pt-3 mt-3 border-t border-border flex flex-col gap-2">
              <LinkButton href={APP_URL} variant="secondary" size="md">
                Sign in
              </LinkButton>
              <LinkButton to="/demo" variant="primary" size="md">
                Request a demo
              </LinkButton>
            </div>
          </div>
        </nav>
      ) : null}
    </header>
  );
}
